import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  IconSearch, 
  IconArmchair2, 
  IconBed, 
  IconFilter, 
  IconReceipt, 
  IconGridDots, 
  IconLayoutList, 
  IconRefresh, 
  IconX,
  IconFileInvoice,
  IconToolsKitchen3,
  IconBook,
  IconDeviceIpadHorizontal,
  IconBuildingSkyscraper
} from "@tabler/icons-react";
import { iconStroke } from "../config/config";
import ApiClient from "../helpers/ApiClient";
import { getOrderTables } from "../controllers/orders.controller";
import { useTranslation } from "react-i18next";
import { useTheme } from "../contexts/ThemeContext";
import { useStoreSettings } from "../controllers/settings.controller";
import toast from "react-hot-toast";

export default function TableRoomGridPage() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [tables, setTables] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [activeOrders, setActiveOrders] = useState([]);
  
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all"); // 'all' | 'tables' | 'rooms' | 'active'
  const [selectedFloor, setSelectedFloor] = useState("all");
  const [selectedRoomType, setSelectedRoomType] = useState("all");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [tablesRes, roomsRes, roomTypesRes, ordersRes] = await Promise.all([
        getOrderTables().catch(() => ({ data: [] })),
        ApiClient.get('/rooms').catch(() => ({ data: [] })),
        ApiClient.get('/rooms/types').catch(() => ({ data: [] })),
        ApiClient.get('/orders').catch(() => ({ data: [] }))
      ]);

      setTables(tablesRes?.data || []);
      setRooms(roomsRes?.data || []);
      setRoomTypes(roomTypesRes?.data || []);
      setActiveOrders(ordersRes?.data || []);
    } catch (error) {
      console.error(error);
      toast.error(t('Failed to load grid data') || 'Failed to load grid data');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        // Prevent default only if we are specifically handling it, 
        // AppBar might also handle it, so we gently try to focus if we are on this page
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const floors = useMemo(() => {
    const allFloors = tables.map(t => t.floor).filter(Boolean);
    return [...new Set(allFloors)];
  }, [tables]);

  const uniqueRoomTypes = useMemo(() => {
    const types = rooms.map(r => r.room_type_name).filter(Boolean);
    return [...new Set(types)];
  }, [rooms]);

  const processedTables = useMemo(() => {
    return tables.map(table => {
      const tableOrders = activeOrders.filter(o => o.table_id === table.id);
      return {
        ...table,
        isOccupied: tableOrders.length > 0,
        orderCount: tableOrders.length
      };
    });
  }, [tables, activeOrders]);

  const processedRooms = useMemo(() => {
    return rooms.map(room => {
      return {
        ...room,
        isOccupied: room.active_booking_id !== null,
        isDirty: room.housekeeping_status && room.housekeeping_status.toLowerCase() !== 'clean'
      };
    });
  }, [rooms]);

  const filteredTables = useMemo(() => {
    if (filterType === 'rooms' || filterType === 'room_active') return [];
    
    return processedTables.filter(table => {
      // Search
      if (searchQuery && !table.table_title.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      // Floor filter
      if (selectedFloor !== 'all' && table.floor !== selectedFloor) {
        return false;
      }
      // Active filter
      if ((filterType === 'active' || filterType === 'table_active') && !table.isOccupied) {
        return false;
      }
      return true;
    });
  }, [processedTables, filterType, searchQuery, selectedFloor]);

  const filteredRooms = useMemo(() => {
    if (filterType === 'tables' || filterType === 'table_active') return [];
    
    return processedRooms.filter(room => {
      // Search
      if (searchQuery && !room.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      // Room Type filter
      if (selectedRoomType !== 'all' && room.room_type_name !== selectedRoomType) {
        return false;
      }
      // Active filter
      if ((filterType === 'active' || filterType === 'room_active') && !room.isOccupied) {
        return false;
      }
      return true;
    });
  }, [processedRooms, filterType, searchQuery, selectedRoomType]);

  const handleTableClick = (table) => {
    navigate('/dashboard/pos', { state: { preSelectTable: table.id, preSelectDiningOption: 'dinein' } });
  };

  const handleRoomClick = (room) => {
    if (!room.active_booking_id && !room.isOccupied) {
      toast.error(`Room ${room.name} is available (not checked-in). Only checked-in rooms can be selected for POS orders.`, { icon: '⚠️', duration: 4000 });
      return;
    }
    navigate('/dashboard/pos', { state: { preSelectRoom: room.name, preSelectDiningOption: 'room' } });
  };

  const clearSearch = () => {
    setSearchQuery("");
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const isDark = theme === 'black';

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-screen ${isDark ? 'bg-neutral-900' : 'bg-gray-50'}`}>
        <div className="flex flex-col items-center">
          <IconRefresh className="animate-spin text-restro-green-light mb-4" size={40} />
          <span className={isDark ? 'text-gray-300' : 'text-gray-500'}>{t('Loading...') || 'Loading...'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-4 md:p-6 h-screen overflow-y-auto ${isDark ? 'bg-neutral-900 text-gray-100' : 'bg-gray-50 text-gray-800'}`}>
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold">{t('Tables & Rooms') || 'Tables & Rooms'}</h1>
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} mt-0.5`}>
            {t('Interactive visual floor & room map for rapid order placement') || 'Interactive visual floor & room map for rapid order placement'}
          </p>
        </div>

        {/* Quick Navigation Toolbar */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* POS Navigation Link */}
          <button 
            onClick={() => navigate('/dashboard/pos')}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-emerald-50 text-emerald-800 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800 rounded-xl hover:opacity-90 transition-all font-medium text-xs shadow-sm hover:scale-[1.02] active:scale-95"
            title="Go to POS Billing"
          >
            <IconDeviceIpadHorizontal size={16} stroke={iconStroke} />
            <span>{t('POS') || 'POS'}</span>
          </button>

          {/* Orders Navigation Link */}
          <button 
            onClick={() => navigate('/dashboard/orders')}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-blue-50 text-blue-800 border border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800 rounded-xl hover:opacity-90 transition-all font-medium text-xs shadow-sm hover:scale-[1.02] active:scale-95"
            title="Go to Orders & Kitchen"
          >
            <IconToolsKitchen3 size={16} stroke={iconStroke} />
            <span>{t('Orders') || 'Orders'}</span>
          </button>

          {/* Invoices Navigation Link */}
          <button 
            onClick={() => navigate('/dashboard/invoices')}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-purple-50 text-purple-800 border border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800 rounded-xl hover:opacity-90 transition-all font-medium text-xs shadow-sm hover:scale-[1.02] active:scale-95"
            title="Go to Invoices & Bills"
          >
            <IconFileInvoice size={16} stroke={iconStroke} />
            <span>{t('Invoices') || 'Invoices'}</span>
          </button>

          {/* Room Status Grid Navigation Link */}
          <button 
            onClick={() => navigate('/dashboard/pms/rooms')}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-amber-50 text-amber-800 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800 rounded-xl hover:opacity-90 transition-all font-medium text-xs shadow-sm hover:scale-[1.02] active:scale-95"
            title="Go to Hotel PMS Room Status Grid"
          >
            <IconBed size={16} stroke={iconStroke} />
            <span>{t('Room Status') || 'Room Status'}</span>
          </button>

          {/* DayBook Navigation Link */}
          <button 
            onClick={() => navigate('/dashboard/financial/daybook')}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-teal-50 text-teal-800 border border-teal-200 dark:bg-teal-950/40 dark:text-teal-300 dark:border-teal-800 rounded-xl hover:opacity-90 transition-all font-medium text-xs shadow-sm hover:scale-[1.02] active:scale-95"
            title="Go to Financial DayBook"
          >
            <IconBook size={16} stroke={iconStroke} />
            <span>{t('DayBook') || 'DayBook'}</span>
          </button>

          {/* Refresh Button */}
          <button 
            onClick={loadData}
            className={`p-2 rounded-xl border transition-colors flex items-center justify-center ${isDark ? 'border-neutral-700 bg-neutral-800 hover:bg-neutral-700' : 'border-gray-200 bg-white hover:bg-gray-100'} shadow-sm hover:scale-[1.02] active:scale-95`}
            title="Refresh Grid Data"
          >
            <IconRefresh size={18} stroke={iconStroke} />
          </button>
        </div>
      </div>

      {/* Controls Section */}
      <div className="flex flex-col lg:flex-row gap-4 mb-8">
        {/* Search */}
        <div className="relative w-full lg:w-96 flex-shrink-0">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <IconSearch size={18} className={isDark ? 'text-gray-400' : 'text-gray-500'} stroke={iconStroke} />
          </div>
          <input
            ref={searchInputRef}
            type="text"
            className={`block w-full pl-10 pr-10 py-3 rounded-2xl border outline-none transition-shadow focus:ring-2 focus:ring-restro-green/50 ${
              isDark 
                ? 'bg-neutral-800 border-neutral-700 text-white placeholder-gray-400' 
                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500 shadow-sm'
            }`}
            placeholder={t('Search tables or rooms... (Ctrl+K)') || 'Search tables or rooms... (Ctrl+K)'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <button onClick={clearSearch} className={`p-1 rounded-full hover:bg-opacity-10 ${isDark ? 'hover:bg-white text-gray-400' : 'hover:bg-black text-gray-500'}`}>
                <IconX size={16} stroke={iconStroke} />
              </button>
            </div>
          )}
        </div>

        {/* Filter Pills */}
        <div className={`flex items-center gap-2 overflow-x-auto pb-2 lg:pb-0 hide-scrollbar`}>
          <div className={`flex items-center px-3 py-1.5 rounded-lg border mr-2 ${isDark ? 'border-neutral-700 bg-neutral-800' : 'border-gray-200 bg-white'}`}>
            <IconFilter size={16} className={`mr-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} stroke={iconStroke} />
            <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{t('Filter') || 'Filter'}:</span>
          </div>
          
          {[
            { id: 'all', label: 'All' },
            { id: 'tables', label: 'Tables' },
            { id: 'rooms', label: 'Rooms' },
            { id: 'table_active', label: 'Table Active' },
            { id: 'room_active', label: 'Room Active' },
            { id: 'active', label: 'All Active' },
          ].map(filter => (
            <button
              key={filter.id}
              onClick={() => setFilterType(filter.id)}
              className={`whitespace-nowrap px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                filterType === filter.id
                  ? 'bg-restro-green text-white shadow-md'
                  : isDark 
                    ? 'bg-neutral-800 text-gray-300 hover:bg-neutral-700 border border-neutral-700' 
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 shadow-sm'
              }`}
            >
              {t(filter.label) || filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="space-y-8 pb-12">
        
        {/* Tables Section */}
        {(filterType === 'all' || filterType === 'tables' || filterType === 'active' || filterType === 'table_active') && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <IconArmchair2 className="text-restro-green" size={24} stroke={iconStroke} />
                {t('Tables') || 'Tables'}
                <span className={`text-sm font-normal px-2 py-0.5 rounded-full ${isDark ? 'bg-neutral-800 text-gray-400' : 'bg-gray-200 text-gray-600'}`}>
                  {filteredTables.length}
                </span>
              </h2>
            </div>

            {floors.length > 0 && (
              <div className="flex gap-2 mb-4 overflow-x-auto hide-scrollbar pb-2">
                <button
                  onClick={() => setSelectedFloor('all')}
                  className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    selectedFloor === 'all'
                      ? (isDark ? 'bg-white text-black' : 'bg-gray-800 text-white')
                      : (isDark ? 'bg-neutral-800 text-gray-400 hover:bg-neutral-700' : 'bg-gray-200 text-gray-600 hover:bg-gray-300')
                  }`}
                >
                  {t('All Floors') || 'All Floors'}
                </button>
                {floors.map(floor => (
                  <button
                    key={floor}
                    onClick={() => setSelectedFloor(floor)}
                    className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      selectedFloor === floor
                        ? (isDark ? 'bg-white text-black' : 'bg-gray-800 text-white')
                        : (isDark ? 'bg-neutral-800 text-gray-400 hover:bg-neutral-700' : 'bg-gray-200 text-gray-600 hover:bg-gray-300')
                    }`}
                  >
                    {floor}
                  </button>
                ))}
              </div>
            )}

            {filteredTables.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-3">
                {filteredTables.map(table => (
                  <button
                    key={`table-${table.id}`}
                    onClick={() => handleTableClick(table)}
                    className={`relative flex flex-col items-center justify-center p-4 rounded-2xl border transition-all hover:scale-[1.02] active:scale-95 text-left h-32 ${
                      isDark ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-gray-100 shadow-sm'
                    } ${
                      table.isOccupied ? 'border-l-4 border-l-amber-500' : 'border-l-4 border-l-emerald-500'
                    }`}
                  >
                    <div className="absolute top-3 right-3">
                      <div className={`w-3 h-3 rounded-full shadow-sm ${table.isOccupied ? 'bg-amber-500' : 'bg-emerald-500'}`}></div>
                    </div>
                    
                    <h3 className="text-xl font-bold mb-1 truncate w-full text-center">{table.table_title}</h3>
                    
                    <div className={`flex items-center gap-1 text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      <IconArmchair2 size={14} stroke={iconStroke} />
                      <span>{table.seating_capacity || 2}</span>
                    </div>

                    {table.isOccupied ? (
                      <span className="text-xs font-semibold px-2 py-1 rounded-md bg-amber-500/10 text-amber-600 dark:text-amber-400">
                        {table.orderCount} {table.orderCount === 1 ? 'Order' : 'Orders'}
                      </span>
                    ) : (
                      <span className="text-xs font-medium px-2 py-1 rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                        {t('Available') || 'Available'}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className={`p-8 text-center rounded-2xl border border-dashed ${isDark ? 'border-neutral-700' : 'border-gray-300'}`}>
                <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>{t('No tables found matching your criteria.') || 'No tables found matching your criteria.'}</p>
              </div>
            )}
          </section>
        )}

        {/* Rooms Section */}
        {(filterType === 'all' || filterType === 'rooms' || filterType === 'active' || filterType === 'room_active') && (
          <section className={filterType === 'all' || filterType === 'active' ? 'mt-8' : ''}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <IconBed className="text-indigo-500" size={24} stroke={iconStroke} />
                {t('Rooms') || 'Rooms'}
                <span className={`text-sm font-normal px-2 py-0.5 rounded-full ${isDark ? 'bg-neutral-800 text-gray-400' : 'bg-gray-200 text-gray-600'}`}>
                  {filteredRooms.length}
                </span>
              </h2>
            </div>

            {uniqueRoomTypes.length > 0 && (
              <div className="flex gap-2 mb-4 overflow-x-auto hide-scrollbar pb-2">
                <button
                  onClick={() => setSelectedRoomType('all')}
                  className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    selectedRoomType === 'all'
                      ? (isDark ? 'bg-white text-black' : 'bg-gray-800 text-white')
                      : (isDark ? 'bg-neutral-800 text-gray-400 hover:bg-neutral-700' : 'bg-gray-200 text-gray-600 hover:bg-gray-300')
                  }`}
                >
                  {t('All Types') || 'All Types'}
                </button>
                {uniqueRoomTypes.map(type => (
                  <button
                    key={type}
                    onClick={() => setSelectedRoomType(type)}
                    className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      selectedRoomType === type
                        ? (isDark ? 'bg-white text-black' : 'bg-gray-800 text-white')
                        : (isDark ? 'bg-neutral-800 text-gray-400 hover:bg-neutral-700' : 'bg-gray-200 text-gray-600 hover:bg-gray-300')
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            )}

            {filteredRooms.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-3">
                {filteredRooms.map(room => {
                  const isOcc = room.isOccupied;
                  const isDirty = room.isDirty;

                  return (
                    <button
                      key={`room-${room.id}`}
                      onClick={() => handleRoomClick(room)}
                      className={`relative flex flex-col items-center justify-center p-4 rounded-2xl border transition-all hover:scale-[1.02] active:scale-95 text-left h-32 ${
                        isDark ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-gray-100 shadow-sm'
                      } border-l-4 ${
                        isOcc ? 'border-l-rose-500' : isDirty ? 'border-l-amber-500' : 'border-l-emerald-500'
                      }`}
                    >
                      <div className="absolute top-3 right-3">
                        <div className={`w-3 h-3 rounded-full shadow-sm ${
                          isOcc ? 'bg-rose-500' : isDirty ? 'bg-amber-500' : 'bg-emerald-500'
                        }`}></div>
                      </div>
                      
                      <h3 className="text-xl font-bold mb-1 truncate w-full text-center">{room.name}</h3>
                      
                      <div className={`text-xs mb-2 truncate w-full text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {room.room_type_name}
                      </div>

                      {isOcc ? (
                        <span className="text-xs font-semibold px-2 py-1 rounded-md bg-rose-500/10 text-rose-600 dark:text-rose-400 truncate max-w-full">
                          {room.occupant || (t('Occupied') || 'Occupied')}
                        </span>
                      ) : isDirty ? (
                        <span className="text-xs font-medium px-2 py-1 rounded-md bg-amber-500/10 text-amber-600 dark:text-amber-400 truncate max-w-full">
                          {room.housekeeping_status}
                        </span>
                      ) : (
                        <span className="text-xs font-medium px-2 py-1 rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                          {t('Available') || 'Available'}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className={`p-8 text-center rounded-2xl border border-dashed ${isDark ? 'border-neutral-700' : 'border-gray-300'}`}>
                <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>{t('No rooms found matching your criteria.') || 'No rooms found matching your criteria.'}</p>
              </div>
            )}
          </section>
        )}

      </div>
    </div>
  );
}
